import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Archive,
  HardDrive,
  Calendar,
  ShieldCheck,
  AlertTriangle,
  RefreshCw,
  Loader2,
  Search,
  ChevronRight,
  X,
  FolderArchive,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import {  showError } from '../../../utils/toast';

export interface OracleRmanBackup {
  bs_key?: number | string;
  backup_type?: string;
  status?: string;
  start_time?: string;
  completion_time?: string;
  elapsed_seconds?: number;
  bytes?: number;
  bytes_mb?: number;
  device_type?: string;
  input_type?: string;
  pieces?: number;
  keep?: string;
  keep_until?: string;
  compressed?: boolean | string;
  tag?: string;
  [key: string]: any;
}

export interface OracleBackupSummary {
  archive_log_mode: string;
  backups: number;
  last_backup_at: string | null;
  last_backup_status: string | null;
}

export interface OracleFraDetails {
  configured: boolean;
  limit_mb: number | null;
  used_mb: number;
  reclaimable_mb: number;
  file_count: number;
  used_pct: number | null;
}

interface OracleBackupResponse {
  status: boolean;
  applicable?: boolean;
  fresh?: boolean;
  collected_at?: string;
  data: {
    summary: OracleBackupSummary;
    fra: OracleFraDetails;
    rman_backups: OracleRmanBackup[];
  };
}

interface OracleBackupViewProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

const formatDate = (isoString?: string | null): string => {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch {
    return isoString;
  }
};

const formatMbOrGb = (mb?: number | null): string => {
  if (mb === null || mb === undefined || isNaN(mb)) return '—';
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(1)} MB`;
};

export const OracleBackupView: React.FC<OracleBackupViewProps> = ({
  activeCluster,
  clientId,
  refreshKey = 0,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [backupData, setBackupData] = useState<OracleBackupResponse['data'] | null>(null);
  const [_meta, setMeta] = useState<{ applicable?: boolean; fresh?: boolean; collected_at?: string }>({});

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedBackup, setSelectedBackup] = useState<OracleRmanBackup | null>(null);
  // const [copiedId, setCopiedId] = useState<string | null>(null);

  const fetchBackups = useCallback(
    async (isManual = false) => {
      if (!activeCluster) return;
      if (isManual) setRefreshing(true);
      else setLoading(true);

      try {
        const params: any = { cluster_id: activeCluster };
        if (clientId) params.client_id = clientId;

        const res = await baseapi.get('/db_monitor_dashboard/oracle/backups', { params });
        const resData: OracleBackupResponse = res.data;

        if (resData && resData.status && resData.data) {
          setBackupData(resData.data);
          setMeta({
            applicable: resData.applicable,
            fresh: resData.fresh,
            collected_at: resData.collected_at,
          });
        } else {
          setBackupData(null);
        }
      } catch (err: any) {
        console.error('Failed to fetch Oracle backup data:', err);
        showError(err.response?.data?.message || err.message || 'Failed to load Oracle backup metrics');
        setBackupData(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [activeCluster, clientId]
  );

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups, refreshKey]);

  const summary = backupData?.summary || {
    archive_log_mode: 'UNKNOWN',
    backups: 0,
    last_backup_at: null,
    last_backup_status: null,
  };

  const fra = backupData?.fra || {
    configured: false,
    limit_mb: null,
    used_mb: 0,
    reclaimable_mb: 0,
    file_count: 0,
    used_pct: null,
  };

  const rmanBackups = backupData?.rman_backups || [];

  const isArchiveLogMode = summary.archive_log_mode.toUpperCase() === 'ARCHIVELOG';

  // Filtered RMAN backups
  const filteredBackups = useMemo(() => {
    if (!rmanBackups || rmanBackups.length === 0) return [];
    if (!searchTerm.trim()) return rmanBackups;

    const q = searchTerm.toLowerCase().trim();
    return rmanBackups.filter((b) => {
      return (
        String(b.bs_key || '').toLowerCase().includes(q) ||
        String(b.backup_type || '').toLowerCase().includes(q) ||
        String(b.status || '').toLowerCase().includes(q) ||
        String(b.tag || '').toLowerCase().includes(q) ||
        String(b.device_type || '').toLowerCase().includes(q)
      );
    });
  }, [rmanBackups, searchTerm]);

  return (
    <div className="space-y-4 pb-12 font-sans relative">
      {/* Top Banner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-800/80 rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-500/10 dark:bg-orange-600/20 border border-orange-300 dark:border-orange-500/30 flex items-center justify-center text-orange-600 dark:text-orange-400 shrink-0">
            <Archive className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                Oracle Backups & Recovery
              </h1>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-orange-100 dark:bg-orange-950/60 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800">
                ORACLE RMAN
              </span>
              <span
                className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border flex items-center gap-1 ${
                  isArchiveLogMode
                    ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30'
                    : 'bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/30'
                }`}
              >
                {isArchiveLogMode ? (
                  <>
                    <ShieldCheck className="w-3 h-3 text-emerald-500" />
                    ARCHIVELOG
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-3 h-3 text-amber-500" />
                    NOARCHIVELOG
                  </>
                )}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Recovery Manager (RMAN) backup sets, archive log generation mode, and Fast Recovery Area (FRA) status
            </p>
          </div>
        </div>

        {/* Action Toolbar */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchBackups(true)}
            disabled={loading || refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-600 hover:bg-orange-500 text-white transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {/* Advisory Alert Banner if in NOARCHIVELOG mode */}
      {!isArchiveLogMode && (
        <div className="p-3.5 rounded-xl border border-amber-200 dark:border-amber-800/60 bg-amber-50/70 dark:bg-amber-950/20 text-amber-800 dark:text-amber-300 flex items-start gap-3 shadow-sm">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-xs space-y-1">
            <p className="font-bold">Database Operating in NOARCHIVELOG Mode</p>
            <p className="text-amber-700 dark:text-amber-400/90 leading-relaxed">
              When in NOARCHIVELOG mode, Oracle does not preserve filled redo log groups to archive destinations. Online point-in-time recovery (PITR) is disabled, and database backups require a clean offline shutdown or read-only tablespaces.
            </p>
          </div>
        </div>
      )}

      {/* Top Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Archive Log Mode */}
        <div className="p-3.5 rounded-xl border bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Log Archive Mode
            </span>
            {isArchiveLogMode ? (
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-amber-500" />
            )}
          </div>
          <div className={`text-xl font-bold font-mono ${isArchiveLogMode ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
            {summary.archive_log_mode}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
            {isArchiveLogMode ? 'Point-in-time recovery active' : 'Offline full recovery only'}
          </div>
        </div>

        {/* Total Backups */}
        <div className="p-3.5 rounded-xl border bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Recorded Backups
            </span>
            <FolderArchive className="w-4 h-4 text-orange-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono">
            {summary.backups.toLocaleString()}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
            Total discovered RMAN backup sets
          </div>
        </div>

        {/* Last Backup Time */}
        <div className="p-3.5 rounded-xl border bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Last Backup
            </span>
            <Calendar className="w-4 h-4 text-blue-500" />
          </div>
          <div className="text-sm font-bold text-gray-900 dark:text-white font-mono truncate">
            {summary.last_backup_at ? formatDate(summary.last_backup_at) : 'None recorded'}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1.5">
            <span>Status:</span>
            <span className="font-semibold text-gray-700 dark:text-gray-300">
              {summary.last_backup_status || 'N/A'}
            </span>
          </div>
        </div>

        {/* Fast Recovery Area (FRA) */}
        <div className="p-3.5 rounded-xl border bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Fast Recovery Area
            </span>
            <HardDrive className="w-4 h-4 text-purple-500" />
          </div>
          <div className="flex items-baseline gap-2">
            <div className={`text-xl font-bold font-mono ${fra.configured ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-600 dark:text-gray-400'}`}>
              {fra.configured ? 'Configured' : 'Not Set'}
            </div>
            {fra.used_pct !== null && (
              <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                ({fra.used_pct.toFixed(1)}% used)
              </span>
            )}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
            {fra.configured ? `${formatMbOrGb(fra.used_mb)} used of ${formatMbOrGb(fra.limit_mb)}` : 'FRA not initialized'}
          </div>
        </div>
      </div>

      {/* Fast Recovery Area (FRA) Details Card */}
      <div className="bg-white dark:bg-gray-900/90 rounded-xl border border-gray-200 dark:border-gray-800/80 p-4 shadow-sm space-y-3">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-2.5">
          <div className="flex items-center gap-2">
            <HardDrive className="w-4 h-4 text-orange-500" />
            <h2 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
              Fast Recovery Area (FRA) Storage Breakdown
            </h2>
          </div>
          <span className="text-[11px] font-mono text-gray-500 dark:text-gray-400">
            Files in FRA: {fra.file_count.toLocaleString()}
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
            <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-1">
              FRA Limit
            </span>
            <span className="text-base font-bold text-gray-900 dark:text-white font-mono">
              {formatMbOrGb(fra.limit_mb)}
            </span>
          </div>

          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
            <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-1">
              Used Space
            </span>
            <span className="text-base font-bold text-orange-600 dark:text-orange-400 font-mono">
              {formatMbOrGb(fra.used_mb)}
            </span>
          </div>

          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
            <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-1">
              Reclaimable Space
            </span>
            <span className="text-base font-bold text-emerald-600 dark:text-emerald-400 font-mono">
              {formatMbOrGb(fra.reclaimable_mb)}
            </span>
          </div>

          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
            <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-1">
              Utilization Pct
            </span>
            <span className="text-base font-bold text-gray-900 dark:text-white font-mono">
              {fra.used_pct !== null ? `${fra.used_pct.toFixed(1)}%` : '—'}
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        {fra.configured && fra.limit_mb && fra.limit_mb > 0 && (
          <div className="space-y-1 pt-1">
            <div className="w-full h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 rounded-full ${
                  (fra.used_pct || 0) >= 90
                    ? 'bg-rose-500'
                    : (fra.used_pct || 0) >= 75
                    ? 'bg-amber-500'
                    : 'bg-emerald-500'
                }`}
                style={{ width: `${Math.min(fra.used_pct || 0, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-gray-400 font-mono">
              <span>0%</span>
              <span>Warning: 75%</span>
              <span>Critical: 90%</span>
              <span>100%</span>
            </div>
          </div>
        )}
      </div>

      {/* RMAN Backups History Table */}
      <div className="bg-white dark:bg-gray-900/90 rounded-xl border border-gray-200 dark:border-gray-800/80 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Archive className="w-4 h-4 text-orange-500" />
            <h2 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
              RMAN Backup History
            </h2>
            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
              ({rmanBackups.length} records)
            </span>
          </div>

          {rmanBackups.length > 0 && (
            <div className="relative w-full sm:w-64">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search backup records..."
                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg bg-gray-50 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700/80 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-500"
              />
            </div>
          )}
        </div>

        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center gap-3">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            <p className="text-xs text-gray-500 dark:text-gray-400">Loading Oracle RMAN backups data...</p>
          </div>
        ) : rmanBackups.length === 0 ? (
          <div className="py-14 px-4 text-center max-w-lg mx-auto space-y-3">
            <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-400 flex items-center justify-center mx-auto">
              <Archive className="w-6 h-6" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                No RMAN Backups Recorded
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                No active RMAN backup sets or image copies were discovered in the database controlfile history.
              </p>
            </div>
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60 text-left font-mono text-[11px] text-gray-700 dark:text-gray-300 space-y-1">
              <div className="text-[10px] font-sans font-bold uppercase text-gray-400">Recommended RMAN Command</div>
              <div className="text-orange-600 dark:text-orange-400 selection:bg-orange-500 selection:text-white">
                RMAN&gt; BACKUP DATABASE PLUS ARCHIVELOG;
              </div>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-gray-50/80 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700/80 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <th className="py-2.5 px-3.5">Key / Tag</th>
                  <th className="py-2.5 px-3">Type</th>
                  <th className="py-2.5 px-3">Status</th>
                  <th className="py-2.5 px-3">Start Time</th>
                  <th className="py-2.5 px-3">Completion Time</th>
                  <th className="py-2.5 px-3">Size</th>
                  <th className="py-2.5 px-3">Device</th>
                  <th className="py-2.5 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60 font-sans text-xs">
                {filteredBackups.map((backup, idx) => (
                  <tr
                    key={backup.bs_key || idx}
                    className="hover:bg-gray-50/70 dark:hover:bg-gray-800/40 transition-colors"
                  >
                    <td className="py-2.5 px-3.5 font-mono font-bold text-gray-900 dark:text-white">
                      {backup.bs_key || backup.tag || `#${idx + 1}`}
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                        {backup.backup_type || backup.input_type || 'BACKUP'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                        {backup.status || 'COMPLETED'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-gray-700 dark:text-gray-300">
                      {formatDate(backup.start_time)}
                    </td>
                    <td className="py-2.5 px-3 text-gray-700 dark:text-gray-300">
                      {formatDate(backup.completion_time)}
                    </td>
                    <td className="py-2.5 px-3 font-mono font-semibold text-gray-900 dark:text-white">
                      {formatMbOrGb(backup.bytes_mb || (backup.bytes ? backup.bytes / (1024 * 1024) : null))}
                    </td>
                    <td className="py-2.5 px-3 text-gray-600 dark:text-gray-400 font-mono text-[11px]">
                      {backup.device_type || 'DISK'}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <button
                        onClick={() => setSelectedBackup(backup)}
                        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-orange-600 dark:text-orange-400 transition-colors cursor-pointer"
                        title="View details"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Backup Details Drawer */}
      {selectedBackup &&
        createPortal(
          <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="w-full max-w-lg bg-white dark:bg-gray-900 h-full shadow-2xl flex flex-col border-l border-gray-200 dark:border-gray-800 overflow-hidden">
              <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/40">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-lg bg-orange-500/10 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400">
                    <Archive className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                      RMAN Backup #{selectedBackup.bs_key || selectedBackup.tag}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Type: {selectedBackup.backup_type || 'RMAN SET'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedBackup(null)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Raw Backup Details
                  </label>
                  <pre className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 text-[11px] font-mono text-gray-800 dark:text-gray-200 max-h-72 overflow-y-auto">
                    {JSON.stringify(selectedBackup, null, 2)}
                  </pre>
                </div>
              </div>

              <div className="p-3.5 border-t border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/40 flex justify-end">
                <button
                  onClick={() => setSelectedBackup(null)}
                  className="px-4 py-1.5 text-xs font-medium rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default OracleBackupView;
